import React, { useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Employees } from '../../../../../assets/icons/v3/employees.svg';
import { COLLABORATORS_HOME_ROUTE, HOME_ROUTE, METRICS_NE_ROUTE } from '../../../../constants/auth';
import { CLICK_ACTION, GO_TO_METRICS, SCREEN_HOME } from '../../../../constants/tagManager';
import { ICardData, IMetricsOnboardingCard } from '../../../../interfaces/cards';
import TagManagerService from '../../../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../translations/commons';
import { TRANSLATIONS_METRICS_NE } from '../../../../translations/metrics';
import { redirectToPage, toLocaleNumberFormat } from '../../../../utils/utils';
import BdsButton from '../../Buttons/BdsButton/BdsButton';
import StackedCirclesChart from '../../Charts/StackedCirclesChart/StackedCirclesChart';
import TimelineChart from '../../Charts/TimelineChart/TimelineChart';
import Card from '../Card/Card';
import EmptyCard from '../EmptyCard/EmptyCard';
import ErrorCard from '../ErrorCard/ErrorCard';

import './MetricsOnboardingCard.scss';

const MetricsOnboardingCard = ({
  title,
  data,
  stackedCircleChartData,
  timelineChartData,
  loading,
  error,
  action,
  colorScheme = 'collaborators',
  isPercentage = false
}: IMetricsOnboardingCard) => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<ICardData[]>([]);

  const { t } = useTranslation();

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => b.value - a.value);
  }, [data]);

  const hasInvitedMembers = useMemo(() => {
    return stackedCircleChartData[0]?.value > 0;
  }, [stackedCircleChartData]);

  const hasActiveMembers = useMemo(() => {
    return stackedCircleChartData[1]?.value > 0;
  }, [stackedCircleChartData]);

  const hasNotNewOnboarded = useMemo(() => {
    return timelineChartData.every((item) => item.value === 0);
  }, [timelineChartData]);

  const isLoading = useMemo(() => {
    return loading && !error;
  }, [loading, error]);

  const isEmptyState = useMemo(() => {
    return !hasInvitedMembers && !hasActiveMembers && !loading && !error;
  }, [hasActiveMembers, hasInvitedMembers, loading, error]);

  useEffect(() => {
    setOrderData(sortedData);
  }, [sortedData]);

  const renderNotActiveMembers = () => {
    const onboardingCard = !hasActiveMembers
      ? TRANSLATIONS_METRICS_NE.onboardingCard.noActive
      : TRANSLATIONS_METRICS_NE.onboardingCard.noNewActive;

    const { title, description, button } = onboardingCard;

    return (
      <div className="metrics-onboarding-card-not-active">
        <div className="not-active-texts">
          <span className="title">{t(title)}</span>
          <span className="description">{t(description)}</span>
        </div>
        <BdsButton size="s" action={() => redirectToPage(navigate, COLLABORATORS_HOME_ROUTE)} variant="inverse">
          {t(button)}
        </BdsButton>
      </div>
    );
  };

  const redirectToMetrics = () => {
    TagManagerService.pushEvent(CLICK_ACTION, GO_TO_METRICS, SCREEN_HOME);
    redirectToPage(navigate, METRICS_NE_ROUTE);
  };

  if (isLoading) {
    return (
      <div className="metrics-skeleton-container">
        <Skeleton className="metrics-onboarding-skeleton" variant="rectangular" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="metrics-onboarding-states" title={title}>
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={action}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          className="metrics-onboarding-error"
        />
      </Card>
    );
  }

  if (isEmptyState) {
    return (
      <Card className="metrics-onboarding-states" title={title}>
        <EmptyCard
          primaryText={t(TRANSLATIONS_METRICS_NE.onboardingCard.emptyState.title)}
          secondaryText={t(TRANSLATIONS_METRICS_NE.onboardingCard.emptyState.description)}
          buttonText={t(TRANSLATIONS_METRICS_NE.onboardingCard.emptyState.button)}
          action={() => redirectToPage(navigate, COLLABORATORS_HOME_ROUTE)}
        />
      </Card>
    );
  }

  return (
    <Card
      className="metrics-onboarding-card"
      title={title}
      data-experience="new"
      redirect={document.location.href.slice(-5) == HOME_ROUTE ? redirectToMetrics : undefined}
    >
      <div className="metrics-onboarding-card-container">
        <div className="metrics-onboarding-card-text-container">
          {orderData.map((item, index) => (
            <div key={item.label} className="flex flex-col gap-1">
              <div className={`number ${colorScheme} color-${index}`}>
                {toLocaleNumberFormat(item.value)}
                {isPercentage && '%'}
              </div>
              <div className="label">{item.label}</div>
            </div>
          ))}
        </div>
        <StackedCirclesChart data={stackedCircleChartData} colorScheme={colorScheme} />
        {colorScheme === 'collaborators' &&
          (!hasActiveMembers || hasNotNewOnboarded ? (
            renderNotActiveMembers()
          ) : (
            <div className="metrics-onboarding-card-timeline">
              <TimelineChart
                IconComponent={Employees}
                elements={timelineChartData}
                title={t(TRANSLATIONS_METRICS_NE.onboardingCard.timelineTitle)}
              />
            </div>
          ))}
      </div>
    </Card>
  );
};

export default MetricsOnboardingCard;
